import { useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GraduationCap, BookOpen, ChevronDown, Check } from 'lucide-react';

export function RoleSwitcher() {
  const { profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [switching, setSwitching] = useState(false);

  if (!profile?.is_teacher || profile.role === 'admin') {
    return null;
  }

  const currentRole = profile.active_role || profile.role;

  const switchRole = async (newRole: 'studente' | 'docente') => {
    if (newRole === currentRole) return;

    setSwitching(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          active_role: newRole,
          role: newRole
        })
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: 'Ruolo cambiato',
        description: `Sei ora in modalità ${newRole === 'docente' ? 'Docente' : 'Studente'}`,
      });

      await refreshProfile();

      window.location.href = '/dashboard';
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile cambiare ruolo',
        variant: 'destructive',
      });
    } finally {
      setSwitching(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={switching}
        >
          {currentRole === 'docente' ? (
            <GraduationCap className="w-4 h-4" />
          ) : (
            <BookOpen className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {currentRole === 'docente' ? 'Docente' : 'Studente'}
          </span>
          <ChevronDown className="w-3 h-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Cambia ruolo</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => switchRole('studente')}
          disabled={switching}
          className="cursor-pointer"
        >
          <BookOpen className="w-4 h-4 mr-2" />
          <span>Studente</span>
          {currentRole === 'studente' && (
            <Check className="w-4 h-4 ml-auto text-blue-600" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => switchRole('docente')}
          disabled={switching}
          className="cursor-pointer"
        >
          <GraduationCap className="w-4 h-4 mr-2" />
          <span>Docente</span>
          {currentRole === 'docente' && (
            <Check className="w-4 h-4 ml-auto text-blue-600" />
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
